import { FeatureMetaKey } from "../types/Custom/Types"

interface FeatureMeta {
  VisualDescription: string
  mainTitle: string
  subTitle?: string
  content: string
  infoList: string[]
}

const heatmapMetadata: FeatureMeta = {
  VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/Heatmap.png",
  mainTitle: "Heatmap",
  content:
    "The Heatmap feature visually identifies high-traffic areas in your facility to optimize layout and enhance customer experience.",
  infoList: [
    "Analyzes customer flow patterns and displays peak traffic areas throughout the day.",
    "Summarize customer distribution throughout your facility.",
    "Tailors marketing strategies to busy zones.",
    "Empowering you to optimize promotional placements and facility management.",
  ],
}

const mcpMetadata: FeatureMeta = {
  VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/mcp.png",
  mainTitle: "Most common paths",
  content: "The Most Common Paths feature reveals the frequently traveled routes taken by visitors within the space.",
  infoList: [
    "Identifies the primary pathways used by people.",
    "Analyzes movement patterns to enhance layout efficiency.",
    "Provides insights into high-traffic areas for better resource allocation.",
    "Complements heatmap data for a comprehensive understanding of visitor flow.",
  ],
}

export const featuresMeta: Record<FeatureMetaKey, FeatureMeta> = {
  heatmap: heatmapMetadata,
  old_heatmap: heatmapMetadata,
  faces: {
    VisualDescription:
      "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/face_recognition.png",
    mainTitle: "Faces",
    content: "The Faces module enables facial recognition and analysis capabilities.",
    infoList: [
      "Identify and track individuals across your facility.",
      "Analyze demographic data and visitor patterns.",
      "Enhance security with facial recognition alerts.",
    ],
  },
  dwelling: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/dwelling_areas.png",
    mainTitle: "People count",
    subTitle: "(Dwelling Areas)",
    content: "The Dwelling module helps you analyze how long people spend in specific areas.",
    infoList: [
      "Measure dwell time in different zones of your facility.",
      "Identify bottlenecks and optimize traffic flow.",
      "Improve customer experience by understanding behavior patterns.",
    ],
  },
  gates: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/Gates.png",
    mainTitle: "People count",
    subTitle: "(Gates)",
    content: "Study your facility's performance with an overview of your customers' count in and out of your facility.",
    infoList: [
      "Track customer dwelling time in front of vendors or retail shelves to gauge engagement.",
      "Have access to historical data and analyze trends and patterns of your visitor count.",
      "Get a better understanding of your data with our advanced analytics tool and extract actionable insights.",
    ],
  },
  corridors: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/Corridors.png",
    mainTitle: "People count",
    subTitle: "(Corridors)",
    content: "Study your facility's performance with an overview of your customers' count in and out of your facility.",
    infoList: [
      "Track customer dwelling time in front of vendors or retail shelves to gauge engagement.",
      "Have access to historical data and analyze trends and patterns of your visitor count.",
      "Get a better understanding of your data with our advanced analytics tool and extract actionable insights.",
    ],
  },
  categories: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/categories.png",
    mainTitle: "People count",
    subTitle: "(categories)",
    content: "Study your facility's performance with an overview of your customers' count in and out of your facility.",
    infoList: [
      "Track customer dwelling time in front of vendors or retail shelves to gauge engagement.",
      "Have access to historical data and analyze trends and patterns of your visitor count.",
      "Get a better understanding of your data with our advanced analytics tool and extract actionable insights.",
    ],
  },
  missing_data: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/missing_data.png",
    mainTitle: "People count",
    subTitle: "(Missing Data)",
    content: "Study your facility's performance with an overview of your customers' count in and out of your facility.",
    infoList: [
      "Track customer dwelling time in front of vendors or retail shelves to gauge engagement.",
      "Have access to historical data and analyze trends and patterns of your visitor count.",
      "Get a better understanding of your data with our advanced analytics tool and extract actionable insights.",
    ],
  },
  tenants: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/tenants.png",
    mainTitle: "People count",
    subTitle: "(Tenants)",
    content: "The Tenants module helps you manage multiple tenants in your system.",
    infoList: [
      "Manage tenant accounts and permissions.",
      "Configure tenant-specific settings and features.",
      "Monitor tenant usage and activity.",
    ],
  },
  checkout: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/Counters.png",
    mainTitle: "People count",
    subTitle: "(Checkout counters)",
    content: "Study your facility's performance with an overview of your customers' count in and out of your facility.",
    infoList: [
      "Track customer dwelling time in front of vendors or retail shelves to gauge engagement.",
      "Have access to historical data and analyze trends and patterns of your visitor count.",
      "Get a better understanding of your data with our advanced analytics tool and extract actionable insights.",
    ],
  },
  staff_occupancy: {
    VisualDescription:
      "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/staff_occupancy.png",
    mainTitle: "Staff Occupancy",
    content:
      "The Staff Occupancy feature provides insights into total staff presence and their distribution over time.",
    infoList: [
      "Analyzes overall staff occupancy levels.",
      "Tracks staff counts across different floors or zones.",
      "Detects both seated and standing staff in each area.",
      "Monitors occupancy trends over time for better resource management.",
    ],
  },
  seat_occupancy: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/seat_occupancy.png",
    mainTitle: "Seat Occupancy",
    content: "The Seat Occupancy feature provides insights into total seat utilization and its distribution over time.",
    infoList: [
      "Analyzes overall seat occupancy levels.",
      "Tracks seat counts across different floors or zones.",
      "Detects both occupied and unoccupied seats in each area.",
      "Monitors occupancy trends over time for better space management.",
    ],
  },
  floors_mcp: mcpMetadata,
  cameras_mcp: mcpMetadata,
  cars_analytics: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/cars_analytics.png",
    mainTitle: "Cars analytics",
    content:
      "The Cars Analytics feature offers a comprehensive overview of car traffic, detailing both returning and new vehicles.",
    infoList: [
      "Provides daily car counts at each gate.",
      "Distinguishes between returning cars and new arrivals.",
      "Analyzes average car counts per hour for better traffic management.",
      "Helps identify peak traffic times for improved operational efficiency.",
    ],
  },
  cars_security: {
    VisualDescription: "https://azkavision-resources.s3.eu-west-1.amazonaws.com/feature_lock_images/Group+36879.png",
    mainTitle: "Cars Logs",
    content:
      "The Car Logs Data feature provides a comprehensive history of vehicles that enter the parking facility, enabling detailed tracking and analysis.",
    infoList: [
      "Captures and stores logs of all vehicles entering the entity, including arrival dates and detailed movement history.",
      "Tracks movement between cameras, noting times and locations.",
      "Automatically detects and records car details, including license plate, color, type, brand, and model.",
      "Includes captured photos of each vehicle for visual reference and enhanced management capabilities.",
    ],
  },
}
